import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal data-fetching hook with:
 *  - loading / error state
 *  - stale-response protection
 *  - global invalidation by tag (call invalidate('tenders') after a mutation)
 *
 * Deliberately tiny; can be swapped for TanStack Query without changing page code
 * (pages only consume { data, loading, error, reload }).
 */
type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();

export function invalidate(...tags: string[]) {
  tags.forEach((t) => listeners.get(t)?.forEach((fn) => fn()));
}

export interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[], tags: string[] = []): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const callId = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const id = ++callId.current;
    setLoading(true);
    setError(null);
    fnRef.current()
      .then((res) => {
        if (id === callId.current) setData(res);
      })
      .catch((e: unknown) => {
        if (id === callId.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (id === callId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const tagKey = tags.join('|');
  useEffect(() => {
    if (!tagKey) return;
    const ts = tagKey.split('|');
    ts.forEach((t) => {
      if (!listeners.has(t)) listeners.set(t, new Set());
      listeners.get(t)!.add(reload);
    });
    return () => ts.forEach((t) => listeners.get(t)?.delete(reload));
  }, [tagKey, reload]);

  return { data, loading, error, reload };
}
